import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * A picture shown at its natural size inside a pane, zoomed and panned by a transform
 * (`observer-autogen-plan-projection`, plan CP3): the calibration screen's picture pane.
 *
 * **Copied deliberately, not shared.** The Observers editor (`PolygonPlotter2.tsx:214-218, 482,
 * 1445-1492`) does the same inline in a 2,649-line component; extracting it would be a refactor
 * inside a behaviour change, which the refactoring charter forbids. A later mechanical PR can make
 * both use one hook (spec, Copies).
 *
 * The view is `translate(x, y) scale(scale)` from natural px to the pane's px. Zoom and pan change
 * only the view, never what is drawn in natural px, so an overlay keyed on the fit is not redrawn.
 */
export interface ImageView {
  scale: number;
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

/** Zoom limits, as multiples of the fitted scale. */
export const MIN_ZOOM_OF_FIT = 0.5;
export const MAX_ZOOM_OF_FIT = 16;
/** One wheel notch (deltaY 100) zooms by about this factor. */
const WHEEL_ZOOM_PER_100 = 1.2;

/** The whole picture contained in the pane, centred. A pane not yet measured shows 1:1. */
export function fitView(natural: Size, pane: Size): ImageView {
  if (!(pane.width > 0 && pane.height > 0 && natural.width > 0 && natural.height > 0)) {
    return { scale: 1, x: 0, y: 0 };
  }
  const scale = Math.min(pane.width / natural.width, pane.height / natural.height);
  return {
    scale,
    x: (pane.width - natural.width * scale) / 2,
    y: (pane.height - natural.height * scale) / 2,
  };
}

/** Zoom by `factor` about a pane point, so the natural point under it stays under it. */
export function zoomAbout(
  view: ImageView,
  factor: number,
  about: { x: number; y: number },
  limits: { min: number; max: number },
): ImageView {
  const scale = Math.min(limits.max, Math.max(limits.min, view.scale * factor));
  const k = scale / view.scale;
  return { scale, x: about.x - (about.x - view.x) * k, y: about.y - (about.y - view.y) * k };
}

/** A pane point → the natural pixel under it. */
export const paneToNatural = (view: ImageView, p: { x: number; y: number }) => ({
  x: (p.x - view.x) / view.scale,
  y: (p.y - view.y) / view.scale,
});

/** A natural pixel → where it is drawn in the pane. */
export const naturalToPane = (view: ImageView, p: { x: number; y: number }) => ({
  x: view.x + p.x * view.scale,
  y: view.y + p.y * view.scale,
});

export function useImageZoomPan(natural: Size | null) {
  const paneRef = useRef<HTMLDivElement | null>(null);
  const [pane, setPane] = useState<Size>({ width: 0, height: 0 });
  const [view, setView] = useState<ImageView>({ scale: 1, x: 0, y: 0 });
  const fitScale = natural ? fitView(natural, pane).scale : 1;

  useLayoutEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const measure = () =>
      setPane(prev =>
        prev.width === el.clientWidth && prev.height === el.clientHeight
          ? prev
          : { width: el.clientWidth, height: el.clientHeight },
      );
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = useCallback(() => {
    if (natural) setView(fitView(natural, pane));
  }, [natural, pane]);

  // A new picture size or pane size refits: the admin's zoom is about one picture in one pane.
  const naturalKey = natural ? `${natural.width}x${natural.height}` : '';
  useEffect(() => {
    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naturalKey, pane.width, pane.height]);

  const limitsRef = useRef({ min: MIN_ZOOM_OF_FIT, max: MAX_ZOOM_OF_FIT });
  limitsRef.current = { min: fitScale * MIN_ZOOM_OF_FIT, max: fitScale * MAX_ZOOM_OF_FIT };

  const zoomBy = useCallback((factor: number, about?: { x: number; y: number }) => {
    const el = paneRef.current;
    const centre = about ?? { x: (el?.clientWidth ?? 0) / 2, y: (el?.clientHeight ?? 0) / 2 };
    setView(v => zoomAbout(v, factor, centre, limitsRef.current));
  }, []);

  const panBy = useCallback((dx: number, dy: number) => {
    setView(v => ({ ...v, x: v.x + dx, y: v.y + dy }));
  }, []);

  /*
   * The wheel, NOT passive: a React `onWheel` is passive, so it cannot stop the page scrolling
   * under the picture while it zooms.
   */
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const factor = WHEEL_ZOOM_PER_100 ** (-e.deltaY / 100);
      zoomBy(factor, { x: e.clientX - rect.left, y: e.clientY - rect.top });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  /*
   * The view as of the last render, read through a ref: a drag's mousemove listener is bound once,
   * at the press, so it must map through the view as it is NOW — zoomed or panned mid-drag — not
   * as it was when the drag began.
   */
  const viewRef = useRef(view);
  viewRef.current = view;

  /** A client point (a pointer event's) → the natural pixel under it, through the current view. */
  const clientToNatural = useCallback((clientX: number, clientY: number) => {
    const rect = paneRef.current?.getBoundingClientRect();
    return paneToNatural(viewRef.current, {
      x: clientX - (rect?.left ?? 0),
      y: clientY - (rect?.top ?? 0),
    });
  }, []);

  return {
    paneRef,
    view,
    /** The zoom as a percentage of the fit: 100 right after a fit. */
    zoomPercent: Math.round((view.scale / (fitScale || 1)) * 100),
    fit,
    zoomBy,
    panBy,
    clientToNatural,
  };
}
